/**
 * ONE QUESTION = ONE CARD, in the agents-island quick-view.
 *
 * A goal-scoped decision (`decision.task_id` empty, `goal_id` set) blocks EVERY task in the goal's plan:
 * each of them names the same decision id in `blockedByDecisions`, and the read folds that one Decision
 * back onto every task that named it (`attachTaskDecisionPrompts`, common-lib) — by design, not by
 * accident. Rendered one row per blocked TASK, that shows the reader the same question three times, under
 * three titles that are identical up to the truncation point, over a pill counting three.
 *
 * So the island collapses them HERE, at the render/selector boundary, and nowhere else: the decorated,
 * UNGROUPED task array keeps feeding the buckets, the tab counts, `unanswerableCardIds` and the Tasks
 * screen. Nothing is stripped off a task row — a card merely points at the tasks it stands for.
 *
 * ## The rules, and why each one is a rule
 *
 *  - **The group key is the `decisionId`, nothing else.** Never the question TEXT: the harness's duplicate
 *    guard is scope-local, so two ids asking the same words are two verdicts and folding them would answer
 *    one and silently leave the other open.
 *  - **Many-to-many.** A task may hold several open questions (`decisionPrompts` is an array). It appears
 *    under EVERY one of their cards and never also as a bare row — a bare row would be a fourth place the
 *    same ask is listed.
 *  - **A row whose question could not be READ is never grouped** (`decisionPromptsUnavailable`, the
 *    tri-state's third state): it carries no decision id to group on, and it keeps its own row with the
 *    existing "couldn't load / open the goal" treatment.
 *  - **The canonical prompt is the FIRST occurrence** in the list's existing order, and the card appears
 *    at the position of that first occurrence. Nothing is re-sorted — the order the Tasks-list comparator
 *    already produced is the order the cards read in.
 *
 * ## What a card may NOT say
 *
 * A card stands for N tasks, but it must never claim an answer will release them. A goal-scoped settle
 * walks the goal and RELEASES the matching branch while RETIRING the others (`AgentTaskDecisionEdges`),
 * and the branch a task belongs to (`DecisionBlocker.requiredChoice`) never reaches the client payload —
 * so "releases 3 tasks" would be a sentence the client has no way to know is true. The card lists the
 * titles under "Waiting on this answer" and stops there.
 *
 * Ported 1:1 to Swift and Kotlin (`AgentsQuickView.swift` / `AgentsQuickView.kt`) with the same eight test
 * names. **This module imports nothing** — not even a type — so every surface's test can load it without
 * the app's dependency graph (see `orion-web/CLAUDE.md`, "a unit test must not import the app's
 * dependency graph").
 */

/** The structural minimum of a prompt: the id the grouping keys on. The real payload is richer
 *  (`TaskDecisionPrompt`: `kind`, `question`, `options`, `planVersion`, `dueBy`) and rides through
 *  untouched — this module never reads a field beyond the id. */
export interface WaitingPrompt {
  decisionId: string;
}

/** The two fields a waiting row is grouped by. Satisfied structurally by `QuickViewTask` and by the
 *  server's decorated AgentTask — deliberately NOT either of those types, so this file stays import-free. */
export interface WaitingCardTask {
  decisionPrompts?: WaitingPrompt[];
  decisionPromptsUnavailable?: boolean;
}

/** The prompt type the caller's own row carries, preserved through the grouping so a card hands back the
 *  REAL prompt (question, options, `planVersion`) and not this module's structural minimum. */
type PromptOf<T extends WaitingCardTask> = NonNullable<T['decisionPrompts']>[number];

/**
 * One rendered card of the "waiting on you" list.
 *
 * `question` — a single open question, and every waiting task that named it (`tasks.length >= 1`).
 * `row` — a waiting row with nothing groupable about it: no question at all (an ACTION_NEEDED /
 * PAUSED task the agent simply parked), or a question that could not be read.
 */
export type WaitingCard<T extends WaitingCardTask> =
  | { kind: 'question'; decisionId: string; prompt: PromptOf<T>; tasks: T[] }
  | { kind: 'row'; task: T };

/**
 * Collapse an ALREADY SORTED, ALREADY DECORATED list of waiting rows into the cards the island renders.
 *
 * Pure: it neither reorders nor mutates its input, and it copies no field off a task. The count the island
 * states is `groupWaitingCards(rows).length` — cards, not blocked tasks.
 */
export function groupWaitingCards<T extends WaitingCardTask>(rows: readonly T[]): WaitingCard<T>[] {
  const cards: WaitingCard<T>[] = [];
  // decisionId → the card already emitted for it, so a later task joins the card at its FIRST
  // appearance's position rather than opening a second one further down.
  const byDecision = new Map<string, { kind: 'question'; decisionId: string; prompt: PromptOf<T>; tasks: T[] }>();

  for (const task of rows) {
    // The tri-state's third state FIRST, exactly as the row component orders it: a task that names a
    // blocker we could not resolve has no id to group on, and must keep saying so on its own row.
    // (`taskDecisionsUnavailable` is the same precedence — the flag is only ever set when no prompt
    // arrived, but reading it first means a malformed row degrades to its own row rather than to silence.)
    if (task.decisionPromptsUnavailable === true) {
      cards.push({ kind: 'row', task });
      continue;
    }
    const prompts = task.decisionPrompts ?? [];
    if (prompts.length === 0) {
      cards.push({ kind: 'row', task });
      continue;
    }
    for (const prompt of prompts) {
      const existing = byDecision.get(prompt.decisionId);
      if (existing) {
        // Guard against the same task naming one decision twice (a duplicated blocker edge): a card lists
        // each waiting task once.
        if (!existing.tasks.includes(task)) existing.tasks.push(task);
        continue;
      }
      const card = {
        kind: 'question' as const,
        decisionId: prompt.decisionId,
        // D8: the FIRST occurrence's prompt is canonical — `planVersion` is echoed verbatim on the
        // verdict, so a later copy of the same decision must never replace the one that was rendered.
        prompt: prompt as PromptOf<T>,
        tasks: [task],
      };
      byDecision.set(prompt.decisionId, card);
      cards.push(card);
    }
  }

  return cards;
}

/** How many TASKS a card stands for — 1 for a plain row, `tasks.length` for a question. Exported because
 *  three surfaces and the BI (`task_count`) all need the same answer, and `kind === 'row' ? 1 : …` written
 *  out four times is four places it can be written as `rows.length`. */
export function waitingCardTaskCount<T extends WaitingCardTask>(card: WaitingCard<T>): number {
  return card.kind === 'question' ? card.tasks.length : 1;
}
